import type { Priority, Task } from './types'

/** Today at midnight, for due-date comparisons that should ignore the clock. */
function startOfToday() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

export function parseDate(value: string | null): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Parses a due date as a calendar date rather than an instant.
 *
 * The API sends a DATE column as `2026-08-02T00:00:00Z`. Feeding that to
 * `new Date()` and reading local components shifts it a day backwards for
 * anybody west of UTC — a task due on the 2nd displays as the 1st, and shows
 * as overdue a day early. Taking the YYYY-MM-DD prefix and building a local
 * midnight keeps the date meaning the day it says.
 */
export function parseDueDate(value: string | null): Date | null {
  if (!value) return null
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

/**
 * Due dates read as words near the present and as a date further out, because
 * "tomorrow" is instantly meaningful and "2026-08-14" is not.
 */
export function formatDue(value: string | null): string {
  const due = parseDueDate(value)
  if (!due) return ''

  const days = Math.round((due.getTime() - startOfToday().getTime()) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  if (days < 0) return `${Math.abs(days)}d late`
  if (days <= 6) return due.toLocaleDateString(undefined, { weekday: 'short' })
  return due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function isOverdue(task: Task): boolean {
  if (!task.dueDate || task.statusDone || task.archivedAt) return false
  const due = parseDueDate(task.dueDate)
  return due !== null && due < startOfToday()
}

export function formatDateTime(value: string): string {
  const d = parseDate(value)
  if (!d) return ''
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** Relative time for activity and comments, falling back to a date after a week. */
export function formatRelative(value: string): string {
  const d = parseDate(value)
  if (!d) return ''
  const secs = Math.round((Date.now() - d.getTime()) / 1000)

  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86_400) return `${Math.floor(secs / 3600)}h ago`
  if (secs < 604_800) return `${Math.floor(secs / 86_400)}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Input[type=date] wants YYYY-MM-DD. The API already sends the date in that
 * form at the front of the timestamp, so slicing it is both simplest and the
 * only version that cannot drift across a timezone boundary.
 */
export function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : ''
}

/**
 * Priority is shown as a monospace marker rather than a colour.
 *
 * A dense board already spends colour on tags, lateness and completion; adding
 * four more priority colours makes none of them readable. `!!` and `!` are
 * unmistakable, cost no colour, and read naturally to people who live in
 * terminals.
 */
export function priorityMark(priority: Priority): string {
  if (priority === 'urgent') return '!!'
  if (priority === 'high') return '!'
  return ''
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const PRIORITIES: Priority[] = ['urgent', 'high', 'medium', 'low']

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/**
 * A stable colour per person, so the same face always has the same avatar
 * without storing anything. Hues are spread across the wheel and kept at a
 * muted saturation so a row of avatars does not shout.
 */
export function avatarHue(id: string): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) % 360
  return hash
}

export function formatBytes(n: number): string {
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`
  if (n >= 1 << 10) return `${Math.round(n / (1 << 10))} KB`
  return `${n} B`
}

export function formatTokens(n: number): string {
  if (n >= 1000) return `~${(n / 1000).toFixed(1)}k tokens`
  return `~${n} tokens`
}

/** The handle used to @mention somebody: the local part of their address. */
export function handleFor(email: string): string {
  return email.split('@')[0] ?? email
}
